import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { toast } from 'react-toastify';
import PageForm from "../../templates/PageForm";
import ProgressBar from "../../atoms/ProgressBar";
import Tab from "../../molecules/Tab";
import SpanList, { ErrorItemType } from "../../atoms/SpanList";
import TextEdit from "../../molecules/TextEdit";

const BLOCK_LIST_PAGE = "/blocklist/0/40/createdAt/DESC";
const MARGIN_TOP_DEFAULT = 380;

interface ErrorItem {
    registerNumber: string;
    reason: string;
}

interface ProcessDetails {
    success: string[];
    erros: ErrorItem[];
    invalidos: ErrorItem[];
}

interface BlockListResponse {
    message: string;
    nameFile: string;
    dtStartProcess: string;
    nameUser: string;
    status: string;
    processDetails: ProcessDetails;
    apiVersion: string;
    apiName: string;
}

interface LocationState {
    responseData: BlockListResponse;
}

const BlockListChanged = () => {
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [content, setContent] = useState<BlockListResponse | null>(null);
    const [error, setError] = useState<string | null>(null);
    const api = useRef('');
    const navigate = useNavigate();
    const location = useLocation();
    const [tabIndex, setTabIndex] = useState<number>(0);

    const calculateWindowHeight = () => {
        setWindowHeight(window.innerHeight - MARGIN_TOP_DEFAULT);
    };
    const [windowHeight, setWindowHeight] = useState<number>(window.innerHeight - MARGIN_TOP_DEFAULT);

    useEffect(() => {
        // Get the response data from location state
        try {
            const state = location.state as LocationState;
            if (state && state.responseData) {
                setContent(state.responseData);

                if (state.responseData.status === 'COMPLETED') {
                    toast.success('Processamento concluído com sucesso!');
                } else if (state.responseData.status === 'FAILED') {
                    toast.error('Processamento falhou!');
                }
            } else {
                // If no state is available, show an error
                setError('Nenhum dado de processamento encontrado. Por favor, envie o arquivo novamente.');
            }
        } catch (err) {
            console.error('Error accessing location state:', err);
            setError('Erro ao carregar dados do processamento');
        } finally {
            setIsLoading(false);
        }

        // Add window resize event listener
        window.addEventListener('resize', calculateWindowHeight);
        calculateWindowHeight();

        return () => {
            window.removeEventListener('resize', calculateWindowHeight);
        };
    }, [location]);

    const formatDate = (dateString: string) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('pt-BR');
    };

    // Get CSS color class based on status
    const getCssColorBy = (status: string | undefined) => {
        if (!status) return 'bg-gray-500';

        const cssClazz: Record<string, string> = {
            'PROCESSING': 'bg-blue-500',
            'COMPLETED': 'bg-green-500',
            'FAILED': 'bg-red-500'
        };
        return cssClazz[status];
    };

    return (
        <PageForm
            pageTitle='BlockList Modificada'
            apiVersion={content?.apiVersion || api.current}
            isLoading={isLoading}
            breadcrumb={[{ label: 'Arquivos da blocklist', url: BLOCK_LIST_PAGE }, { label: 'BlockList Modificada', url: '#' }]}
            controlSlot={(
                <ProgressBar
                    label="Processando envio de arquivo..."
                    progress={content?.status === 'PROCESSING' ? 50 : content?.status === 'COMPLETED' ? 100 : 0}
                    visible={content?.status === 'PROCESSING'}
                />
            )}
        >
            {error ? (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4">
                    <strong className="font-bold">Erro!</strong>
                    <span className="block sm:inline"> {error}</span>
                </div>
            ) : isLoading ? (
                <div className="flex justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                </div>
            ) : content ? (
                <div className="bg-white shadow-md rounded-lg p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                        <div className="bg-gray-50 p-4 rounded-md">
                            <p className="text-sm text-gray-500">Arquivo</p>
                            <p className="font-medium">{content.nameFile}</p>
                        </div>

                        <div className="bg-gray-50 p-4 rounded-md">
                            <p className="text-sm text-gray-500">Data de Processamento</p>
                            <p className="font-medium">{formatDate(content.dtStartProcess)}</p>
                        </div>

                        <div className="bg-gray-50 p-4 rounded-md">
                            <p className="text-sm text-gray-500">Usuário</p>
                            <p className="font-medium">{content.nameUser}</p>
                        </div>

                        <div className="bg-gray-50 p-4 rounded-md">
                            <p className="text-sm text-gray-500">Status</p>
                            <p className={`font-medium ${content.status === 'COMPLETED' ? 'text-green-600' :
                                content.status === 'FAILED' ? 'text-red-600' :
                                    content.status === 'PROCESSING' ? 'text-blue-600' : ''
                                }`}>
                                {content.status === 'COMPLETED' ? 'Concluído' :
                                    content.status === 'FAILED' ? 'Falhou' :
                                        content.status === 'PROCESSING' ? 'Processando' : content.status}
                            </p>
                        </div>
                    </div>

                    <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6">
                        <p className="text-blue-700">{content.message}</p>
                    </div>

                    <div className="mt-4">
                        <h2 className="mb-2 text-base text-gray-700 font-medium">
                            Documentos
                            <span className="text-xs text-gray-400 font-normal ml-2">
                                {content.processDetails.success.length + content.processDetails.erros.length + content.processDetails.invalidos.length} registros
                            </span>
                        </h2>

                        <Tab
                            tabItems={[
                                { id: "tab-0", title: 'Sucessos', index: 0, badge: content.processDetails.success.length.toString() },
                                { id: "tab-1", title: 'Erros', index: 1, badge: content.processDetails.erros.length.toString() },
                                { id: "tab-2", title: 'Inválidos', index: 2, badge: content.processDetails.invalidos.length.toString() }
                            ]}
                            tabIndex={Number(tabIndex)}
                            setTabIndex={setTabIndex}
                        />

                        <div className="mt-4">
                            {tabIndex === 0 && (
                                <div className="relative overflow-y-auto" style={{ height: windowHeight }}>
                                    {content.processDetails.success.length > 0 ? (
                                        <SpanList
                                            itemType={ErrorItemType.SUCCESS}
                                            items={content.processDetails.success.map(cpf => ({ label: cpf }))}
                                        />
                                    ) : (
                                        <div className="bg-blue-100 border-t border-b border-blue-500 text-blue-700 px-4 py-3">
                                            <p className="text-sm">Nenhum registro de sucesso.</p>
                                        </div>
                                    )}
                                </div>
                            )}

                            {tabIndex === 1 && (
                                <div className="relative overflow-y-auto" style={{ height: windowHeight }}>
                                    {content.processDetails.erros.length > 0 ? (
                                        <SpanList
                                            itemType={ErrorItemType.ERROR}
                                            items={content.processDetails.erros.map(error => ({
                                                label: error.registerNumber,
                                                tooltip: error.reason
                                            }))}
                                        />
                                    ) : (
                                        <div className="bg-blue-100 border-t border-b border-blue-500 text-blue-700 px-4 py-3">
                                            <p className="text-sm">Nenhum registro de erro.</p>
                                        </div>
                                    )}
                                </div>
                            )}

                            {tabIndex === 2 && (
                                <div className="relative overflow-y-auto" style={{ height: windowHeight }}>
                                    {content.processDetails.invalidos.length > 0 ? (
                                        <SpanList
                                            itemType={ErrorItemType.ERROR}
                                            items={content.processDetails.invalidos.map(item => ({
                                                label: item.registerNumber,
                                                tooltip: item.reason
                                            }))}
                                        />
                                    ) : (
                                        <div className="bg-blue-100 border-t border-b border-blue-500 text-blue-700 px-4 py-3">
                                            <p className="text-sm">Nenhum registro de inválidos.</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="mt-8 flex justify-end">
                        <button
                            onClick={() => navigate(BLOCK_LIST_PAGE)}
                            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                        >
                            Voltar para Lista
                        </button>
                    </div>
                </div>
            ) : (
                <div className="text-center p-8">
                    <p className="text-gray-500">Nenhuma informação disponível</p>
                </div>
            )}
        </PageForm>
    );
}

export default BlockListChanged;
